const configs = {
    dev: {
        // Running Zeus locally
        // API_DOMAIN: "https://dev.mytutor.cloud",
        // LOGIN_PAGE: "https://dev.mytutor.cloud/tutors/login.html",
        // AUTH_COOKIES_DOMAIN: "dev.mytutor.cloud",
        // Leveraging the test environment
        API_DOMAIN: "https://zeus.test.mytutor.cloud",
        LOGIN_PAGE: "https://zeus.test.mytutor.cloud/tutors/login.html",
        AUTH_COOKIES_DOMAIN: "zeus.test.mytutor.cloud",
    },

    cypress: {
        API_DOMAIN: "https://dev.mytutor.cloud",
        LOGIN_PAGE: "/signup/1", // hack to check redirection in isolation
    },

    // Frontend and backend on the same domain → empty API_DOMAIN uses relative paths
    test: {
        API_DOMAIN: "",
        LOGIN_PAGE: "/tutors/login.html",
        LOGOUT_PAGE: "/tutors/secure/logout",
        AUTH_COOKIES_DOMAIN: "zeus.test.mytutor.cloud",
    },

    // Frontend and backend on the same domain → empty API_DOMAIN uses relative paths
    prod: {
        API_DOMAIN: "",
        LOGIN_PAGE: "/tutors/login.html",
        ENV: "prod",
        LOGOUT_PAGE: "/tutors/secure/logout",
        AUTH_COOKIES_DOMAIN: "www.mytutor.co.uk",
    },
};

// Default to production environment config
const config = configs[process.env.REACT_APP_STAGE] || configs.prod;

const createUrl = (
    version,
    resourcePath,
    domain = config.API_DOMAIN,
    apiBasePath = "rest",
    resourceBasePath = "mt"
) => {
    return (
        `${domain}` +
        `/${apiBasePath}` +
        `/v${version}` +
        (resourceBasePath ? `/${resourceBasePath}` : "") +
        `/${resourcePath}`
    );
};

export const resourcePaths = {
    tutors: {
        signup: "tutors/signup",
        application: {
            root: "tutors/application",
            submit: "tutors/application/submit",
            canApply: "tutors/application/can-apply",
            nextStep: "tutors/application/next-step",
            saveIntroAcceptance: "tutors/application/intro/acceptance",
            qualifications: "tutors/application/qualifications",
            subjects: "tutors/application/subjects",
            degreeCertificate: "tutors/application/degree-certificate",
            gcse: "subjects/gcse",
        },
        subjects: {
            root: "tutors/subjects",
        },
    },
    qualifications: {
        root: "qualifications",
        degreeTypes: "qualifications/degree-types",
        additional: "qualifications?isSchoolLeaving=false",
        schoolLeaving: "qualifications/school-leaving",
        grades: qualificationLevel =>
            `qualifications/${qualificationLevel}/grades`,
    },
    subjects: {
        root: "subjects",
        academic: "subjects/academic",
        coding: "subjects/coding",
        gcse: "subjects/gcse",
    },
    personal: {
        workEligibilities: "personal/work-eligibilities",
        nationalities: "personal/nationalities",
        genders: "personal/genders",
    },
    cookies: {
        expose: "cookies/expose",
    },
    universities: {
        names: "universities/names",
        detail: universityName => `universities/name/${universityName}`,
    },
    schools: {
        formValidator: "validation/forms/students/itemized",
        formValidatorSummary: "validation/forms/students/summarized",
    },
};

export const resourceUrls = {
    intro: {
        submit: createUrl(
            1,
            resourcePaths.tutors.application.saveIntroAcceptance
        ),
    },
    apply: {
        nextStep: createUrl(1, resourcePaths.tutors.application.nextStep),
        getCanApply: createUrl(1, resourcePaths.tutors.application.canApply),
    },
    additionalQualifications: {
        getAdditionalQualifications: createUrl(
            1,
            resourcePaths.qualifications.additional
        ),
        submit: createUrl(1, resourcePaths.tutors.application.qualifications),
    },
    codingLanguages: {
        getCodingSubjects: createUrl(1, resourcePaths.subjects.coding),
        submit: createUrl(1, resourcePaths.tutors.application.subjects),
    },
    currentStudy: {
        getDegreeTypes: createUrl(1, resourcePaths.qualifications.degreeTypes),
        submit: createUrl(1, resourcePaths.tutors.application.root),
    },
    gcseQualifications: {
        getSubjects: createUrl(1, resourcePaths.subjects.gcse),
        submit: createUrl(2, resourcePaths.tutors.application.gcse),
    },
    qualifications: {
        getSchoolLeavingQualifications: createUrl(
            1,
            resourcePaths.qualifications.schoolLeaving
        ),
        getSubjects: createUrl(1, resourcePaths.subjects.academic),
        getGrades: qualificationLevel =>
            createUrl(
                1,
                resourcePaths.qualifications.grades(qualificationLevel)
            ),
        submit: createUrl(1, resourcePaths.tutors.application.qualifications),
    },
    personalContact: {
        getNationalities: createUrl(1, resourcePaths.personal.nationalities),
        getGenders: createUrl(1, resourcePaths.personal.genders),
        submitPersonalContact: createUrl(
            1,
            resourcePaths.tutors.application.root
        ),
    },
    degreeCertificate: {
        submitCertificate: createUrl(
            1,
            resourcePaths.tutors.application.degreeCertificate
        ),
    },
    workEligibility: {
        getEligibilities: createUrl(
            1,
            resourcePaths.personal.workEligibilities
        ),
        submit: createUrl(1, resourcePaths.tutors.application.root),
    },
    healthDeclaration: {
        submit: createUrl(1, resourcePaths.tutors.application.root),
    },
    tutoringSubjects: {
        getEligibleTutorsSubjects: createUrl(
            1,
            resourcePaths.tutors.subjects.root
        ),
        submitSubjects: createUrl(1, resourcePaths.tutors.application.subjects),
    },
    submit: {
        submit: createUrl(1, resourcePaths.tutors.application.submit),
    },
    cookies: {
        expose: createUrl(
            1,
            resourcePaths.cookies.expose,
            config.API_DOMAIN,
            "api",
            ""
        ),
    },
    signup: {
        getUniversityNames: createUrl(
            1,
            resourcePaths.universities.names,
            config.API_DOMAIN,
            "api",
            ""
        ),
        getUniversity: universityName =>
            createUrl(
                1,
                resourcePaths.universities.detail(universityName),
                config.API_DOMAIN,
                "api",
                null
            ),
        submit: createUrl(
            1,
            resourcePaths.tutors.signup,
            config.API_DOMAIN,
            "api",
            null
        ),
    },
    schools: {
        formValidator: createUrl(
            1,
            resourcePaths.schools.formValidator,
            config.API_DOMAIN,
            "api",
            ""
        ),
        formValidatorSummary: createUrl(
            1,
            resourcePaths.schools.formValidatorSummary,
            config.API_DOMAIN,
            "api",
            ""
        ),
    },
};

export default config;
